import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../classes/BaseComponent';
import { BaseService } from '../classes/BaseService';
import { Global } from '../classes/Global';
import { StorageHelper } from '../helpers/StorageHelper';
import { Log } from '../helpers/Log';
import { UrlValidator } from '../helpers/UrlValidater';
import { CustomListService } from '../custom-list.service';
import { WishlistService } from '../wishlist.service';
import { BlocklistService } from '../blocklist.service';
import { OwnListService } from '../own-list.service';
import { PriceHelper } from '../helpers/PriceHelper';
import { ListHelper } from '../helpers/ListHelper';
import { BrowserNavigationHelper } from '../helpers/BrowserNavigationHelper';
import { ModalComponent } from '../modal/modal.component';
import { MovieRatingsFilters } from '../movie-ratings-sliders/movie-ratings-sliders.component';
import { ToastService } from '../toast/toast.service';
import { StringHelper } from '../helpers/StringHelper';

@Component({
  selector: 'app-custom-list-detail',
  templateUrl: './custom-list-detail.component.html',
  styleUrls: ['./custom-list-detail.component.scss'],
  providers: [ WishlistService, BlocklistService, OwnListService ]
})
export class CustomListDetailComponent extends BaseComponent implements OnInit {

  static SORT_ORIGINAL = "customListOriginalOrder";
  static SORT_TITLE = "alphabetical";
  static SORT_PRICE = "price";
  static SORT_RELEASE_DATE = "releaseDate";
  static SORT_IMDB = "imdbRating";
  static SORT_ROTTEN = "rottenRating";
  static SORT_METACRITIC = "metacriticRating";
  static SORT_LETTERBOXD = "letterboxdRating";
  static FILTER_ALL = "all";
  static FILTER_WISHLIST = "wishlist";
  static FILTER_NOT_WISHLIST = "notOnWishlist";
  static FILTER_PURCHASED = "alreadyPurchased";
  static FILTER_NOT_PURCHASED = "notPurchased";
  static FILTER_REDUCED = "reducedOnly";

  detail: any = null;
  allItems: Array<any> = [];
  items: Array<any> = [];
  sortValues: Array<string> = [
    CustomListDetailComponent.SORT_ORIGINAL,
    CustomListDetailComponent.SORT_TITLE,
    CustomListDetailComponent.SORT_PRICE,
    CustomListDetailComponent.SORT_RELEASE_DATE,
    CustomListDetailComponent.SORT_IMDB,
    CustomListDetailComponent.SORT_ROTTEN,
    CustomListDetailComponent.SORT_METACRITIC,
    CustomListDetailComponent.SORT_LETTERBOXD
  ];
  sortKey = CustomListDetailComponent.SORT_ORIGINAL;
  filterValues: Array<string> = [
    CustomListDetailComponent.FILTER_ALL,
    CustomListDetailComponent.FILTER_WISHLIST,
    CustomListDetailComponent.FILTER_NOT_WISHLIST
  ];
  customListFilterAll = CustomListDetailComponent.FILTER_ALL;
  filterKey = CustomListDetailComponent.FILTER_ALL;
  purchaseFilterValues: Array<string> = [
    CustomListDetailComponent.FILTER_ALL,
    CustomListDetailComponent.FILTER_PURCHASED,
    CustomListDetailComponent.FILTER_NOT_PURCHASED
  ];
  purchaseFilterKey = CustomListDetailComponent.FILTER_ALL;
  reducedFilterValues: Array<string> = [
    CustomListDetailComponent.FILTER_ALL,
    CustomListDetailComponent.FILTER_REDUCED
  ];
  reducedFilterKey = CustomListDetailComponent.FILTER_ALL;
  movieRatingsFilters: MovieRatingsFilters = new MovieRatingsFilters();
  listId = "";
  isLoading = false;
  hasError = false;
  showLoginRequired = false;
  sessionToken = "";
  isOptionsMenuOpen = false;
  isLikingCustomList = false;
  restoreItemScrollY = -1;
  restoreItemScrollAttempts = 0;
  filterTranslationKeyOverrides: { [key: string]: string } = {
    wishlist: "onWishlist"
  };

  @ViewChild("deleteListConfirmationModal", {static: false}) deleteListConfirmationModal;
  @ViewChild("authentication", {static: false}) authentication;

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!event || !event.target) {
      return;
    }

    let target = event.target;
    while (target) {
      if (target.classList && target.classList.contains("detail-menu")) {
        return;
      }
      target = target.parentElement;
    }

    this.closeOptionsMenu();
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    this.prepareBackNavigation();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customListService: CustomListService,
    private wishlistService: WishlistService,
    private blocklistService: BlocklistService,
    private ownlistService: OwnListService,
    private toastService: ToastService
  ) {
    super();
  }

  ngOnInit() {
    UrlValidator.validateWishlistUrl(this.route, this.router);

    this.sessionToken = StorageHelper.getInstance().get(StorageHelper.keys.sessionToken) || "";

    this.route.params.subscribe(params => {
      this.listId = params['listId'] || "";
      this.loadDetail();
    });
  }

  loadDetail() {
    if (!this.listId) {
      this.hasError = true;
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.detail = null;
    this.allItems = [];
    this.items = [];
    this.loadPersistedFilterSettings();

    this.customListService.getCustomListDetail(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.listId,
      this.sessionToken
    ).subscribe(res => {
      if (res && res["message"] === BaseService.COULDNT_LOAD_USER) {
        Global.clearSaveUserInfos();
        this.showLoginRequired = true;
      }
      else {
        this.detail = this.normalizeDetailResponse(res);
        this.allItems = this.normalizeItemsResponse(res, this.detail);
        this.applyFilterAndSort();
        this.prepareItemScrollRestore();
      }
      this.isLoading = false;
    }, err => {
      Log.getInstance().d(err);
      this.hasError = true;
      this.isLoading = false;
    });
  }

  normalizeDetailResponse(res: any): any {
    if (!res) {
      return {};
    }

    if (res.results) {
      if (res.results.data && !this.hasDetailFields(res.results)) {
        return res.results.data;
      }
      return res.results;
    }

    if (res.data) {
      return res.data;
    }

    return res;
  }

  normalizeItemsResponse(res: any, detail: any): Array<any> {
    let items = [];

    if (detail) {
      if (detail.itemData instanceof Array) {
        items = detail.itemData;
        return this.normalizeGridItems(items);
      }
      if (detail.items instanceof Array) {
        items = detail.items;
        return this.normalizeGridItems(items);
      }
      if (detail.data && detail.data.items instanceof Array) {
        items = detail.data.items;
        return this.normalizeGridItems(items);
      }
      if (detail.data && detail.data.itemData instanceof Array) {
        items = detail.data.itemData;
        return this.normalizeGridItems(items);
      }
    }

    if (res && res.results) {
      if (res.results.itemData instanceof Array) {
        items = res.results.itemData;
        return this.normalizeGridItems(items);
      }
      if (res.results.items instanceof Array) {
        items = res.results.items;
        return this.normalizeGridItems(items);
      }
      if (res.results.data && res.results.data.items instanceof Array) {
        items = res.results.data.items;
        return this.normalizeGridItems(items);
      }
      if (res.results.data && res.results.data.itemData instanceof Array) {
        items = res.results.data.itemData;
        return this.normalizeGridItems(items);
      }
    }

    return [];
  }

  hasDetailFields(detail: any): boolean {
    return detail && (
      detail.ID !== undefined ||
      detail.id !== undefined ||
      detail.listId !== undefined ||
      detail.title !== undefined ||
      detail.description !== undefined ||
      detail.itemListLength !== undefined ||
      detail.itemData instanceof Array ||
      detail.items instanceof Array
    );
  }

  getListsUrl(): string {
    const returnUrl = this.getStoredListReturnUrl();
    if (returnUrl) {
      return returnUrl;
    }

    const section = this.getDetailRouteSection();
    if (this.isMyListRouteSection(section)) {
      return "/" + [
        Global.getCountry(),
        Global.getStoreUrl(),
        this.getString(Global.getMediaTypeForWishlistRequest() + "Url"),
        this.getString("myListsUrl")
      ].join('/');
    }

    const urlKey = this.isCommunityListRouteSection(section) ? "communityListsUrl" : "editorialListsUrl";
    return "/" + [
      Global.getCountry(),
      Global.getStoreUrl(),
      this.getString(Global.getMediaTypeForWishlistRequest() + "Url"),
      this.getString("discoverUrl"),
      this.getString(urlKey)
    ].join('/');
  }

  getStoredListReturnUrl(): string {
    const saved = this.getStoredListReturnState(false);
    return saved ? saved.returnUrl : "";
  }

  getStoredListReturnState(allowReturnUrlMatch: boolean): any {
    if (typeof sessionStorage === "undefined") {
      return null;
    }

    try {
      const value = sessionStorage.getItem(this.getListReturnUrlStorageKey());
      if (!value) {
        return null;
      }

      const saved = JSON.parse(value);
      if (!saved || !saved.returnUrl || !saved.targetUrl) {
        return null;
      }

      const timestamp = Number(saved.timestamp);
      if (isNaN(timestamp) || Date.now() - timestamp > 30 * 60 * 1000) {
        return null;
      }

      const currentPath = this.getPathFromUrl(this.router.url);
      const targetPath = this.getPathFromUrl(saved.targetUrl);
      const returnPath = this.getPathFromUrl(saved.returnUrl);
      if (targetPath !== currentPath && (allowReturnUrlMatch !== true || returnPath !== currentPath)) {
        return null;
      }

      return saved;
    }
    catch (e) {
      Log.getInstance().d(e);
    }

    return null;
  }

  getListReturnUrlStorageKey(): string {
    return "customListReturnUrl:" + Global.getCountry() + ":" + Global.getStore() + ":" + Global.getMediaType();
  }

  prepareBackNavigation() {
    const savedReturn = this.getStoredListReturnState(true);
    if (typeof sessionStorage === "undefined" || !savedReturn) {
      return;
    }

    try {
      sessionStorage.setItem(this.getListBackNavigationStorageKey(), JSON.stringify({
        targetUrl: savedReturn.targetUrl,
        returnUrl: savedReturn.returnUrl,
        timestamp: Date.now()
      }));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getListBackNavigationStorageKey(): string {
    return "customListBackNavigation:" + Global.getCountry() + ":" + Global.getStore() + ":" + Global.getMediaType();
  }

  prepareItemNavigation() {
    if (typeof sessionStorage === "undefined") {
      return;
    }

    try {
      sessionStorage.setItem(this.getItemScrollStorageKey(), JSON.stringify({
        scrollY: window.scrollY || 0,
        url: this.router.url.split("#")[0],
        timestamp: Date.now(),
        pending: true
      }));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getItemScrollStorageKey(): string {
    return "customListDetailItemScroll:v1:" + Global.getCountry() + ":" + Global.getStore() + ":" + Global.getMediaType() + ":" + this.listId;
  }

  prepareItemScrollRestore() {
    this.restoreItemScrollY = this.getSavedItemScrollPosition();
    this.restoreItemScrollAttempts = 0;
    this.restoreItemScrollPositionSoon();
  }

  getSavedItemScrollPosition(): number {
    if (typeof sessionStorage === "undefined") {
      return -1;
    }

    try {
      const value = sessionStorage.getItem(this.getItemScrollStorageKey());
      if (!value) {
        return -1;
      }

      const saved = JSON.parse(value);
      if (!saved || saved.pending !== true) {
        return -1;
      }

      const scrollY = Number(saved.scrollY);
      const timestamp = Number(saved.timestamp);
      if (isNaN(scrollY) || scrollY < 0 || isNaN(timestamp) || Date.now() - timestamp > 30 * 60 * 1000) {
        return -1;
      }

      if (this.getPathFromUrl(saved.url) !== this.getPathFromUrl(this.router.url)) {
        return -1;
      }

      return scrollY;
    }
    catch (e) {
      Log.getInstance().d(e);
    }

    return -1;
  }

  restoreItemScrollPositionSoon() {
    if (this.restoreItemScrollY < 0) {
      return;
    }

    this.restoreItemScrollAttempts++;
    setTimeout(() => {
      if (this.restoreItemScrollY < 0) {
        return;
      }

      const documentElement = document.documentElement;
      const scrollHeight = Math.max(
        document.body ? document.body.scrollHeight : 0,
        documentElement ? documentElement.scrollHeight : 0
      );
      const maxScrollY = Math.max(0, scrollHeight - window.innerHeight);
      if (maxScrollY + 30 < this.restoreItemScrollY && this.restoreItemScrollAttempts < 20) {
        this.restoreItemScrollPositionSoon();
        return;
      }

      window.scrollTo(0, this.restoreItemScrollY);
      if (this.restoreItemScrollAttempts < 20 && Math.abs(window.scrollY - this.restoreItemScrollY) > 30) {
        this.restoreItemScrollPositionSoon();
      }
      else {
        if (Math.abs(window.scrollY - this.restoreItemScrollY) <= 30) {
          this.consumeSavedItemScrollPosition();
        }
        this.restoreItemScrollY = -1;
      }
    }, 250);
  }

  consumeSavedItemScrollPosition() {
    if (typeof sessionStorage === "undefined") {
      return;
    }

    try {
      const value = sessionStorage.getItem(this.getItemScrollStorageKey());
      if (!value) {
        return;
      }
      const saved = JSON.parse(value);
      saved.pending = false;
      sessionStorage.setItem(this.getItemScrollStorageKey(), JSON.stringify(saved));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getPathFromUrl(url: any): string {
    if (url === undefined || url === null) {
      return "";
    }
    return String(url).split("?")[0].split("#")[0];
  }

  getDetailRouteSection(): string {
    const path = this.router.url.split("?")[0].split("#")[0];
    const parts = path.split("/");
    if (parts.length > 5 && this.isDiscoverRouteSegment(parts[4])) {
      return parts[5];
    }
    return parts.length > 4 ? parts[4] : "";
  }

  isDiscoverRouteSegment(segment: string): boolean {
    return segment === this.getString("discoverUrl") ||
      segment === "discover" ||
      segment === "entdecken" ||
      segment === "decouvrir" ||
      segment === "descubrir";
  }

  isMyListRouteSection(section: string): boolean {
    return section === this.getString("myListsUrl") ||
      section === "my-lists" ||
      section === "meine-listen";
  }

  canManageCurrentList(): boolean {
    return this.isMyListRouteSection(this.getDetailRouteSection());
  }

  isCommunityListRouteSection(section: string): boolean {
    return section === this.getString("communityListsUrl") ||
      section === "community-lists";
  }

  getListDetailUrl(): string {
    if (!this.listId) {
      return "";
    }
    return this.getListsUrl() + "/" + this.listId + "/" + this.getSlug(this.getTitle());
  }

  getAbsoluteListDetailUrl(): string {
    return this.getAbsoluteAppUrl(this.getListDetailUrl());
  }

  getAbsoluteAppUrl(route: string): string {
    if (!route) {
      return "";
    }
    if (route.indexOf("http://") === 0 || route.indexOf("https://") === 0) {
      return route;
    }

    const origin = window.location.origin || (window.location.protocol + "//" + window.location.host);
    const basePath = this.getBasePath();
    const routePath = route.charAt(0) === "/" ? route.substr(1) : route;
    return origin + basePath + routePath;
  }

  getBasePath(): string {
    let baseHref = "";
    const baseElement = document.querySelector("base");
    if (baseElement) {
      baseHref = baseElement.getAttribute("href") || "";
    }

    if (!baseHref || baseHref === "/" || baseHref === "./") {
      return "/";
    }

    if (baseHref.indexOf("http://") === 0 || baseHref.indexOf("https://") === 0) {
      const link = document.createElement("a");
      link.href = baseHref;
      baseHref = link.pathname || "/";
    }

    if (baseHref.charAt(0) !== "/") {
      baseHref = "/" + baseHref;
    }
    if (baseHref.charAt(baseHref.length - 1) !== "/") {
      baseHref += "/";
    }
    return baseHref;
  }

  getTitle(): string {
    if (!this.detail) {
      return "";
    }
    return this.detail.title || this.detail.name || this.detail.listName || "";
  }

  getDescription(): string {
    if (!this.detail) {
      return "";
    }
    return this.detail.description || this.detail.desc || "";
  }

  showDetailMetaRow(): boolean {
    return !!this.detail;
  }

  getCustomListLikeCount(): number {
    if (!this.detail) {
      return 0;
    }

    return this.normalizeNumber(
      this.detail.likeCounter !== undefined ? this.detail.likeCounter :
      this.detail.likeCount !== undefined ? this.detail.likeCount :
      this.detail.likes !== undefined ? this.detail.likes :
      this.detail.favoriteCount !== undefined ? this.detail.favoriteCount :
      this.detail.favorites !== undefined ? this.detail.favorites :
      this.detail.upvotes !== undefined ? this.detail.upvotes :
      this.detail.voteCount
    );
  }

  isCustomListLiked(): boolean {
    if (!this.detail) {
      return false;
    }

    const value = this.detail.userLiked !== undefined ? this.detail.userLiked :
      this.detail.isLiked !== undefined ? this.detail.isLiked :
      this.detail.liked !== undefined ? this.detail.liked :
      this.detail.hasLiked !== undefined ? this.detail.hasLiked :
      this.detail.likedByUser !== undefined ? this.detail.likedByUser :
      this.detail.userHasLiked !== undefined ? this.detail.userHasLiked :
      this.detail.isFavorite !== undefined ? this.detail.isFavorite :
      this.detail.favorite;

    return this.isTruthyValue(value) || this.isCustomListLikedLocally();
  }

  getLocalLikeStorageKey(): string {
    const sessionToken = this.getSessionToken();
    return "customListLikes_" + (sessionToken ? sessionToken : "anonymous");
  }

  getLocalLikedListIds(): Array<string> {
    const savedListIds = StorageHelper.getInstance().get(this.getLocalLikeStorageKey());
    return savedListIds instanceof Array ? savedListIds : [];
  }

  isCustomListLikedLocally(): boolean {
    if (!this.listId || !this.getSessionToken()) {
      return false;
    }
    return this.getLocalLikedListIds().indexOf(String(this.listId)) >= 0;
  }

  saveCustomListLikedLocally(isLiked: boolean) {
    if (!this.listId || !this.getSessionToken()) {
      return;
    }

    const listIds = this.getLocalLikedListIds();
    const listId = String(this.listId);
    const index = listIds.indexOf(listId);
    if (isLiked && index < 0) {
      listIds.push(listId);
    }
    else if (!isLiked && index >= 0) {
      listIds.splice(index, 1);
    }
    StorageHelper.getInstance().save(this.getLocalLikeStorageKey(), listIds);
  }

  getCategoryTags(): Array<any> {
    if (!this.detail) {
      return [];
    }

    const tags = [];
    this.appendCategoryTags(tags, this.detail.tags);
    this.appendCategoryTags(tags, this.detail.tagData);
    this.appendCategoryTags(tags, this.detail.tagList);
    this.appendCategoryTags(tags, this.detail.categories);
    this.appendCategoryTags(tags, this.detail.categoryTags);
    this.appendCategoryTags(tags, this.detail.category);

    return this.dedupeCategoryTags(tags);
  }

  appendCategoryTags(tags: Array<any>, rawTags: any) {
    if (rawTags === undefined || rawTags === null || rawTags === "") {
      return;
    }

    if (rawTags instanceof Array) {
      for (let i = 0; i < rawTags.length; i++) {
        this.appendCategoryTag(tags, rawTags[i]);
      }
      return;
    }

    if (typeof rawTags === "string") {
      const parts = rawTags.split(",");
      for (let j = 0; j < parts.length; j++) {
        this.appendCategoryTag(tags, parts[j]);
      }
      return;
    }

    if (typeof rawTags === "object") {
      const label = this.getCategoryTagLabel(rawTags);
      if (label) {
        tags.push({
          id: this.getCategoryTagId(rawTags),
          label: label
        });
        return;
      }
      for (const key in rawTags) {
        if (rawTags.hasOwnProperty(key)) {
          this.appendCategoryTags(tags, rawTags[key]);
        }
      }
    }
  }

  appendCategoryTag(tags: Array<any>, rawTag: any) {
    const label = this.getCategoryTagLabel(rawTag);
    if (label) {
      tags.push({
        id: this.getCategoryTagId(rawTag),
        label: label
      });
    }
  }

  getCategoryTagLabel(rawTag: any): string {
    if (rawTag === undefined || rawTag === null) {
      return "";
    }

    if (typeof rawTag === "string" || typeof rawTag === "number") {
      return String(rawTag).trim();
    }

    if (typeof rawTag === "object") {
      const label = rawTag.title || rawTag.name || rawTag.label || rawTag.tag || rawTag.text || rawTag.category;
      return label !== undefined && label !== null ? String(label).trim() : "";
    }

    return "";
  }

  getCategoryTagId(rawTag: any): string {
    if (rawTag === undefined || rawTag === null || typeof rawTag !== "object") {
      return "";
    }

    const id = rawTag.ID || rawTag.id || rawTag.tagID || rawTag.tagId || rawTag.value;
    return id === undefined || id === null || id === "" ? "" : String(id);
  }

  getCategoryTagUrl(tag: any): string {
    if (!tag || tag.id === undefined || tag.id === null || tag.id === "") {
      return "";
    }

    return "/" + [
      Global.getCountry(),
      Global.getStoreUrl(),
      this.getString(Global.getMediaTypeForWishlistRequest() + "Url"),
      this.getString("discoverUrl"),
      this.getString("editorialListsUrl"),
      String(tag.id),
      this.getSlug(tag.label)
    ].join('/');
  }

  prepareCategoryTagNavigation(tag: any) {
    const categoryUrl = this.getCategoryTagUrl(tag);
    if (typeof sessionStorage === "undefined" || !categoryUrl) {
      return;
    }

    const path = categoryUrl.split("?")[0].split("#")[0];
    const parts = path.split("/");
    if (parts.length < 7) {
      return;
    }

    try {
      sessionStorage.setItem(this.getPublicCategoryRouteStorageKey(parts), JSON.stringify({
        path: path,
        timestamp: Date.now()
      }));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getPublicCategoryRouteStorageKey(parts: Array<string>): string {
    const hasDiscoverSegment = parts.length > 7 && this.isDiscoverRouteSegment(parts[4]);
    const categoryIndex = hasDiscoverSegment ? 6 : 5;
    const slugIndex = hasDiscoverSegment ? 7 : 6;

    return "customListsCategoryRoute:v1:" +
      parts[1] + ":" +
      parts[2] + ":" +
      parts[3] + ":public:" +
      parts[categoryIndex] + ":" +
      parts[slugIndex];
  }

  isCategoryTagClickable(tag: any): boolean {
    return this.getCategoryTagUrl(tag) !== "";
  }

  getSlug(value: any): string {
    let slug = value === undefined || value === null ? "" : String(value).toLowerCase();
    if (slug.normalize) {
      slug = slug.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }
    slug = slug.replace(/&/g, " and ");
    slug = slug.replace(/[^a-z0-9]+/g, "-");
    slug = slug.replace(/^-+|-+$/g, "");
    return slug || "list";
  }

  dedupeCategoryTags(values: Array<any>): Array<any> {
    const result = [];
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      let exists = false;
      for (let j = 0; j < result.length; j++) {
        if ((result[j].id && value.id && String(result[j].id) === String(value.id)) ||
          String(result[j].label).toLowerCase() === String(value.label).toLowerCase()) {
          exists = true;
          break;
        }
      }
      if (!exists) {
        result.push(value);
      }
    }
    return result;
  }

  toggleCustomListLike(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isLikingCustomList || !this.listId || !this.detail) {
      return;
    }

    const sessionToken = this.getSessionToken();
    if (!sessionToken) {
      this.openAuthentication();
      return;
    }

    const wasLiked = this.isCustomListLiked();
    const previousCount = this.getCustomListLikeCount();
    this.setLocalLikeState(!wasLiked, wasLiked ? Math.max(0, previousCount - 1) : previousCount + 1);
    this.isLikingCustomList = true;

    this.customListService.likeCustomList(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.listId,
      sessionToken,
      wasLiked ? "unlike" : "like"
    ).subscribe(res => {
      if (res && res["message"] === BaseService.COULDNT_LOAD_USER) {
        Global.clearSaveUserInfos();
        this.sessionToken = "";
        this.setLocalLikeState(wasLiked, previousCount);
        this.openAuthentication();
      }
      else {
        this.applyLikeResponse(res);
      }
      this.isLikingCustomList = false;
    }, err => {
      Log.getInstance().d(err);
      this.setLocalLikeState(wasLiked, previousCount);
      this.isLikingCustomList = false;
    });
  }

  setLocalLikeState(isLiked: boolean, likeCount: number) {
    if (!this.detail) {
      return;
    }
    this.detail.userLiked = isLiked ? 1 : 0;
    this.detail.hasLiked = isLiked ? 1 : 0;
    this.detail.likeCounter = likeCount < 0 ? 0 : likeCount;
    this.detail.likeCount = likeCount < 0 ? 0 : likeCount;
    this.saveCustomListLikedLocally(isLiked);
  }

  applyLikeResponse(res: any) {
    const likeData = this.getLikeResponseData(res);
    if (!likeData) {
      return;
    }

    if (likeData.likeCounter !== undefined || likeData.likeCount !== undefined || likeData.likes !== undefined || likeData.favoriteCount !== undefined ||
      likeData.favorites !== undefined || likeData.upvotes !== undefined || likeData.voteCount !== undefined) {
      const updatedLikeCount = this.normalizeNumber(
        likeData.likeCounter !== undefined ? likeData.likeCounter :
        likeData.likeCount !== undefined ? likeData.likeCount :
        likeData.likes !== undefined ? likeData.likes :
        likeData.favoriteCount !== undefined ? likeData.favoriteCount :
        likeData.favorites !== undefined ? likeData.favorites :
        likeData.upvotes !== undefined ? likeData.upvotes :
        likeData.voteCount
      );
      this.detail.likeCounter = updatedLikeCount;
      this.detail.likeCount = updatedLikeCount;
    }

    if (likeData.userLiked !== undefined || likeData.isLiked !== undefined || likeData.liked !== undefined ||
      likeData.hasLiked !== undefined || likeData.likedByUser !== undefined || likeData.userHasLiked !== undefined ||
      likeData.isFavorite !== undefined || likeData.favorite !== undefined) {
      const value = likeData.userLiked !== undefined ? likeData.userLiked :
        likeData.isLiked !== undefined ? likeData.isLiked :
        likeData.liked !== undefined ? likeData.liked :
        likeData.hasLiked !== undefined ? likeData.hasLiked :
        likeData.likedByUser !== undefined ? likeData.likedByUser :
        likeData.userHasLiked !== undefined ? likeData.userHasLiked :
        likeData.isFavorite !== undefined ? likeData.isFavorite :
        likeData.favorite;
      const liked = this.isTruthyValue(value) ? 1 : 0;
      this.detail.userLiked = liked;
      this.detail.hasLiked = liked;
    }
  }

  getLikeResponseData(res: any): any {
    if (!res) {
      return null;
    }
    if (res.additionalInfo) {
      return res.additionalInfo;
    }
    if (res.additional_info) {
      return res.additional_info;
    }
    if (res.results) {
      if (res.results.additionalInfo) {
        return res.results.additionalInfo;
      }
      if (res.results.additional_info) {
        return res.results.additional_info;
      }
      if (res.results.data) {
        return res.results.data;
      }
      return res.results;
    }
    if (res.data) {
      return res.data;
    }
    return res;
  }

  openAuthentication() {
    if (this.authentication && this.authentication.openRegister) {
      this.authentication.openRegister();
    }
  }

  normalizeNumber(value: any): number {
    if (value === undefined || value === null || value === "") {
      return 0;
    }

    const numberValue = Number(value);
    return isNaN(numberValue) ? 0 : numberValue;
  }

  isTruthyValue(value: any): boolean {
    if (value === true || value === 1 || value === "1") {
      return true;
    }
    if (typeof value === "string") {
      const normalizedValue = value.toLowerCase();
      return normalizedValue === "true" || normalizedValue === "yes";
    }
    return false;
  }

  getItemCount(): number {
    if (this.detail && this.detail.itemListLength !== undefined && this.detail.itemListLength !== null) {
      return this.detail.itemListLength;
    }
    if (this.detail && this.detail.count !== undefined && this.detail.count !== null) {
      return this.detail.count;
    }
    return this.items.length;
  }

  hasPublicValue(): boolean {
    return this.detail && (this.detail.isPublic !== undefined || this.detail.public !== undefined);
  }

  isPublicList(): boolean {
    if (!this.detail) {
      return false;
    }
    const value = this.detail.isPublic !== undefined ? this.detail.isPublic : this.detail.public;
    return value === true || value === 1 || value === "1" || value === "true";
  }

  getUserData(): any {
    if (!this.detail) {
      return null;
    }
    return this.detail.userData || this.detail.user || this.detail.authorData || this.detail.authorInfo || null;
  }

  getAuthorUsername(): string {
    const userData = this.getUserData();
    if (userData) {
      return userData.username || userData.userName || userData.name || userData.displayName || "";
    }
    return this.detail && (this.detail.username || this.detail.userName || this.detail.authorName) ?
      (this.detail.username || this.detail.userName || this.detail.authorName) : "";
  }

  getAuthorProfileImage(): string {
    const userData = this.getUserData();
    const image = userData ? (userData.profileImage || userData.profileImageUrl || userData.profileImageURL || userData.avatar || userData.image) :
      (this.detail ? (this.detail.profileImage || this.detail.profileImageUrl || this.detail.profileImageURL || this.detail.avatar) : "");
    return image && image !== "placeholder" ? image : "./assets/images/profile_placeholder.png";
  }

  hasAuthorInfo(): boolean {
    return this.getAuthorUsername() !== "";
  }

  showImagePlaceholder(event: Event) {
    if (!event || !event.target) {
      return;
    }
    const image = <HTMLImageElement>event.target;
    const placeholder = "./assets/images/profile_placeholder.png";

    if (image.src && image.src.indexOf(placeholder.replace("./", "")) >= 0) {
      return;
    }

    image.src = placeholder;
  }

  toggleOptionsMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.isOptionsMenuOpen = !this.isOptionsMenuOpen;
  }

  closeOptionsMenu() {
    this.isOptionsMenuOpen = false;
  }

  deleteList(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeOptionsMenu();

    if (!this.listId || !this.canManageCurrentList()) {
      return;
    }

    this.deleteListConfirmationModal.open();
  }

  onDeleteListConfirmationResult(result) {
    if (result !== ModalComponent.RESULT_YES || !this.listId || !this.canManageCurrentList()) {
      return;
    }

    this.customListService.deleteCustomList(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken,
      this.listId
    ).subscribe(res => {
      if (this.isActionSuccess(res)) {
        this.router.navigateByUrl(this.getListsUrl());
      }
    }, err => {
      Log.getInstance().d(err);
    });
  }

  sortChanged(sortKey: string) {
    this.sortKey = this.isValidSortKey(sortKey) ? sortKey : CustomListDetailComponent.SORT_ORIGINAL;
    this.persistFilterSettings();
    this.applyFilterAndSort();
  }

  filterChanged(filterKey: string) {
    this.filterKey = this.isValidFilterKey(filterKey, this.filterValues) ? filterKey : CustomListDetailComponent.FILTER_ALL;
    this.persistFilterSettings();
    this.applyFilterAndSort();
  }

  purchaseFilterChanged(filterKey: string) {
    this.purchaseFilterKey = this.isValidFilterKey(filterKey, this.purchaseFilterValues) ? filterKey : CustomListDetailComponent.FILTER_ALL;
    this.persistFilterSettings();
    this.applyFilterAndSort();
  }

  reducedFilterChanged(filterKey: string) {
    this.reducedFilterKey = this.isValidFilterKey(filterKey, this.reducedFilterValues) ? filterKey : CustomListDetailComponent.FILTER_ALL;
    this.persistFilterSettings();
    this.applyFilterAndSort();
  }

  applyMovieRatingsFilter(values: MovieRatingsFilters) {
    this.movieRatingsFilters = values || new MovieRatingsFilters();
    this.persistFilterSettings();
    this.applyFilterAndSort();
  }

  resetMovieRatingsFilters() {
    this.movieRatingsFilters = new MovieRatingsFilters();
    this.persistFilterSettings();
    this.applyFilterAndSort();
  }

  resetFiltersAndSort() {
    this.sortKey = CustomListDetailComponent.SORT_ORIGINAL;
    this.filterKey = CustomListDetailComponent.FILTER_ALL;
    this.purchaseFilterKey = CustomListDetailComponent.FILTER_ALL;
    this.reducedFilterKey = CustomListDetailComponent.FILTER_ALL;
    this.movieRatingsFilters = new MovieRatingsFilters();
    this.persistFilterSettings();
    this.applyFilterAndSort();
  }

  hasActiveFiltersOrSort(): boolean {
    return this.sortKey !== CustomListDetailComponent.SORT_ORIGINAL ||
      this.filterKey !== CustomListDetailComponent.FILTER_ALL ||
      this.purchaseFilterKey !== CustomListDetailComponent.FILTER_ALL ||
      this.reducedFilterKey !== CustomListDetailComponent.FILTER_ALL ||
      this.hasActiveMovieRatingsFilters();
  }

  loadPersistedFilterSettings() {
    this.sortKey = CustomListDetailComponent.SORT_ORIGINAL;
    this.filterKey = CustomListDetailComponent.FILTER_ALL;
    this.purchaseFilterKey = CustomListDetailComponent.FILTER_ALL;
    this.reducedFilterKey = CustomListDetailComponent.FILTER_ALL;
    this.movieRatingsFilters = new MovieRatingsFilters();

    if (typeof localStorage === "undefined") {
      return;
    }

    try {
      const value = localStorage.getItem(this.getFilterSettingsStorageKey());
      if (!value) {
        return;
      }

      const saved = JSON.parse(value);
      if (!saved) {
        return;
      }

      if (this.isValidSortKey(saved.sortKey)) {
        this.sortKey = saved.sortKey;
      }
      if (this.isValidFilterKey(saved.filterKey, this.filterValues)) {
        this.filterKey = saved.filterKey;
      }
      if (this.isValidFilterKey(saved.purchaseFilterKey, this.purchaseFilterValues)) {
        this.purchaseFilterKey = saved.purchaseFilterKey;
      }
      if (this.isValidFilterKey(saved.reducedFilterKey, this.reducedFilterValues)) {
        this.reducedFilterKey = saved.reducedFilterKey;
      }

      const ratings = saved.movieRatingsFilters || {};
      this.movieRatingsFilters = new MovieRatingsFilters(
        Number(ratings.imdb) || 0,
        Number(ratings.rotten) || 0,
        Number(ratings.letterboxd) || 0,
        Number(ratings.metacritic) || 0
      );
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  persistFilterSettings() {
    if (typeof localStorage === "undefined") {
      return;
    }

    try {
      localStorage.setItem(this.getFilterSettingsStorageKey(), JSON.stringify({
        sortKey: this.sortKey,
        filterKey: this.filterKey,
        purchaseFilterKey: this.purchaseFilterKey,
        reducedFilterKey: this.reducedFilterKey,
        movieRatingsFilters: {
          imdb: this.movieRatingsFilters ? this.movieRatingsFilters.imdb : 0,
          rotten: this.movieRatingsFilters ? this.movieRatingsFilters.rotten : 0,
          letterboxd: this.movieRatingsFilters ? this.movieRatingsFilters.letterboxd : 0,
          metacritic: this.movieRatingsFilters ? this.movieRatingsFilters.metacritic : 0
        }
      }));
    }
    catch (e) {
      Log.getInstance().d(e);
    }
  }

  getFilterSettingsStorageKey(): string {
    return "customListDetailFilters:v1:" + Global.getCountry() + ":" + Global.getStore() + ":" + Global.getMediaType();
  }

  isValidSortKey(sortKey: string): boolean {
    return this.sortValues.indexOf(sortKey) >= 0;
  }

  isValidFilterKey(filterKey: string, allowedValues: Array<string>): boolean {
    return allowedValues.indexOf(filterKey) >= 0;
  }

  getActiveMovieRatingsFilters(): string {
    const filters = [];
    if (this.movieRatingsFilters.imdb > 0) {
      filters.push(this.getString('imdb'));
    }
    if (this.movieRatingsFilters.rotten > 0) {
      filters.push(this.getString('rottenTomatoes'));
    }
    if (this.movieRatingsFilters.metacritic > 0) {
      filters.push(this.getString('metacritic'));
    }
    if (this.movieRatingsFilters.letterboxd > 0) {
      filters.push(this.getString('letterboxdRating'));
    }
    return filters.length > 0 ? filters.join(', ') : this.getString('all');
  }

  hasActiveMovieRatingsFilters(): boolean {
    return this.movieRatingsFilters && (this.movieRatingsFilters.imdb > 0 || this.movieRatingsFilters.rotten > 0 ||
      this.movieRatingsFilters.metacritic > 0 || this.movieRatingsFilters.letterboxd > 0);
  }

  applyFilterAndSort() {
    const filteredItems = this.getFilteredItems();
    this.items = this.getSortedItems(filteredItems);
  }

  getFilteredItems(): Array<any> {
    const sourceItems = this.allItems || [];
    let filteredItems = sourceItems.slice(0);

    if (this.filterKey === CustomListDetailComponent.FILTER_WISHLIST) {
      filteredItems = filteredItems.filter(item => ListHelper.isItemInWishlist(item, Global.getStore()));
    }
    else if (this.filterKey === CustomListDetailComponent.FILTER_NOT_WISHLIST) {
      filteredItems = filteredItems.filter(item => !ListHelper.isItemInWishlist(item, Global.getStore()));
    }

    if (this.purchaseFilterKey === CustomListDetailComponent.FILTER_PURCHASED) {
      filteredItems = filteredItems.filter(item => ListHelper.isItemInOwnlist(item, Global.getStore()));
    }
    else if (this.purchaseFilterKey === CustomListDetailComponent.FILTER_NOT_PURCHASED) {
      filteredItems = filteredItems.filter(item => !ListHelper.isItemInOwnlist(item, Global.getStore()));
    }

    if (this.reducedFilterKey === CustomListDetailComponent.FILTER_REDUCED) {
      filteredItems = filteredItems.filter(item => this.isItemReduced(item));
    }

    if (this.hasActiveMovieRatingsFilters()) {
      filteredItems = filteredItems.filter(item => this.matchesMovieRatingsFilters(item));
    }

    return filteredItems;
  }

  isItemReduced(item: any): boolean {
    if (!item) {
      return false;
    }
    const dropValue = this.getFirstNumericValue(item, ["priceDropIndicator", "priceHdDropIndicator", "priceSdDropIndicator"]);
    if (dropValue !== null && dropValue !== undefined) {
      return dropValue < 0;
    }
    const currentPrice = this.getSortPrice(item);
    const oldPrice = this.getPreviousPriceFromHistory(item);
    return currentPrice !== null && oldPrice !== null && currentPrice < oldPrice;
  }

  matchesMovieRatingsFilters(item: any): boolean {
    if (this.movieRatingsFilters.imdb > 0 && this.getSortRating(item, ["imdbRating", "imdb", "imdb_score"]) < this.movieRatingsFilters.imdb) {
      return false;
    }
    if (this.movieRatingsFilters.rotten > 0 && this.getSortRating(item, ["tomatoesScore", "rottenTomatoesRating", "rottenTomatoesScore", "rtRating", "rtScore"]) < this.movieRatingsFilters.rotten) {
      return false;
    }
    if (this.movieRatingsFilters.metacritic > 0 && this.getSortRating(item, ["metacritic", "metacriticRating", "metacriticScore"]) < this.movieRatingsFilters.metacritic) {
      return false;
    }
    if (this.movieRatingsFilters.letterboxd > 0 && this.getSortRating(item, ["letterboxdRating", "letterboxdScore"]) < this.movieRatingsFilters.letterboxd) {
      return false;
    }
    return true;
  }

  getSortedItems(items: Array<any>): Array<any> {
    if (!items || items.length < 2) {
      return items || [];
    }

    const sortKey = this.sortKey || CustomListDetailComponent.SORT_ORIGINAL;
    const sortedItems = items.slice(0);

    sortedItems.sort((a, b) => {
      if (sortKey === CustomListDetailComponent.SORT_TITLE) {
        return this.compareStringValues(this.getSortTitle(a), this.getSortTitle(b), a, b);
      }
      if (sortKey === CustomListDetailComponent.SORT_PRICE) {
        return this.compareNumberValues(this.getSortPrice(a), this.getSortPrice(b), a, b, true);
      }
      if (sortKey === CustomListDetailComponent.SORT_RELEASE_DATE) {
        return this.compareNumberValues(this.getSortReleaseDate(a), this.getSortReleaseDate(b), a, b, false);
      }
      if (sortKey === CustomListDetailComponent.SORT_IMDB) {
        return this.compareNumberValues(this.getSortRating(a, ["imdbRating", "imdb", "imdb_score"]), this.getSortRating(b, ["imdbRating", "imdb", "imdb_score"]), a, b, false);
      }
      if (sortKey === CustomListDetailComponent.SORT_ROTTEN) {
        return this.compareNumberValues(this.getSortRating(a, ["tomatoesScore", "rottenTomatoesRating", "rottenTomatoesScore", "rtRating", "rtScore"]), this.getSortRating(b, ["tomatoesScore", "rottenTomatoesRating", "rottenTomatoesScore", "rtRating", "rtScore"]), a, b, false);
      }
      if (sortKey === CustomListDetailComponent.SORT_METACRITIC) {
        return this.compareNumberValues(this.getSortRating(a, ["metacritic", "metacriticRating", "metacriticScore"]), this.getSortRating(b, ["metacritic", "metacriticRating", "metacriticScore"]), a, b, false);
      }
      if (sortKey === CustomListDetailComponent.SORT_LETTERBOXD) {
        return this.compareNumberValues(this.getSortRating(a, ["letterboxdRating", "letterboxdScore"]), this.getSortRating(b, ["letterboxdRating", "letterboxdScore"]), a, b, false);
      }

      return this.compareOriginalOrder(a, b);
    });

    return sortedItems;
  }

  compareStringValues(valueA: string, valueB: string, itemA: any, itemB: any): number {
    const hasA = valueA !== "";
    const hasB = valueB !== "";
    if (hasA && !hasB) {
      return -1;
    }
    if (!hasA && hasB) {
      return 1;
    }
    if (!hasA && !hasB) {
      return this.compareOriginalOrder(itemA, itemB);
    }

    const comparison = valueA.localeCompare(valueB);
    return comparison !== 0 ? comparison : this.compareOriginalOrder(itemA, itemB);
  }

  compareNumberValues(valueA: number, valueB: number, itemA: any, itemB: any, ascending: boolean): number {
    const hasA = valueA !== null && valueA !== undefined && !isNaN(valueA);
    const hasB = valueB !== null && valueB !== undefined && !isNaN(valueB);
    if (hasA && !hasB) {
      return -1;
    }
    if (!hasA && hasB) {
      return 1;
    }
    if (!hasA && !hasB) {
      return this.compareOriginalOrder(itemA, itemB);
    }

    if (valueA === valueB) {
      return this.compareOriginalOrder(itemA, itemB);
    }

    return ascending ? valueA - valueB : valueB - valueA;
  }

  compareOriginalOrder(itemA: any, itemB: any): number {
    return this.getOriginalIndex(itemA) - this.getOriginalIndex(itemB);
  }

  getOriginalIndex(item: any): number {
    if (!item || item.customListOriginalIndex === undefined || item.customListOriginalIndex === null) {
      return 0;
    }
    return item.customListOriginalIndex;
  }

  getSortTitle(item: any): string {
    if (!item) {
      return "";
    }
    const title = item.title || item.itemTitle || item.trackName || item.collectionName || item.name || item.itemName || "";
    return String(title).toLowerCase();
  }

  getSortPrice(item: any): number {
    return this.getFirstPriceValue(item, [
      "price",
      "priceHd",
      "priceHD",
      "priceSd",
      "priceSD",
      "currentPrice",
      "currentPriceValue",
      "priceValue",
      "itemPriceAmount",
      "itemPriceHD",
      "itemPriceHd",
      "itemPriceSD",
      "itemPriceSd",
      "trackPrice",
      "collectionPrice",
      "itemPriceLabel",
      "priceLabel",
      "formattedPrice",
      "currentPriceLabel"
    ]);
  }

  getSortReleaseDate(item: any): number {
    if (!item) {
      return null;
    }

    const releaseValue = this.getFirstFieldValue(item, [
      "releaseDate",
      "itemReleaseDate",
      "firstReleaseDate",
      "trackReleaseDate",
      "collectionReleaseDate"
    ]);
    const dateValue = this.parseDateValue(releaseValue);
    if (dateValue !== null && dateValue !== undefined) {
      return dateValue;
    }

    const yearValue = this.getFirstFieldValue(item, ["productionYear", "year", "releaseYear"]);
    const year = parseInt(String(yearValue), 10);
    return isNaN(year) ? null : new Date(year, 0, 1).getTime();
  }

  getSortRating(item: any, keys: Array<string>): number {
    return this.getFirstNumericValue(item, keys);
  }

  getFirstFieldValue(item: any, keys: Array<string>): any {
    if (!item) {
      return null;
    }

    for (let i = 0; i < keys.length; i++) {
      const value = item[keys[i]];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }

    return null;
  }

  getFirstNumericValue(item: any, keys: Array<string>): number {
    if (!item) {
      return null;
    }

    for (let i = 0; i < keys.length; i++) {
      const numericValue = this.parseNumericValue(item[keys[i]]);
      if (numericValue !== null && numericValue !== undefined) {
        return numericValue;
      }
    }

    return null;
  }

  parseNumericValue(value: any): number {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    if (typeof value === "number") {
      return value;
    }
    if (typeof value !== "string") {
      return null;
    }

    const match = value.replace(",", ".").match(/-?\d+(\.\d+)?/);
    if (!match) {
      return null;
    }

    const numericValue = Number(match[0]);
    return isNaN(numericValue) ? null : numericValue;
  }

  parseDateValue(value: any): number {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    if (typeof value === "number") {
      if (value > 1000 && value < 3000) {
        return new Date(value, 0, 1).getTime();
      }
      return value;
    }

    const timestamp = Date.parse(String(value));
    return isNaN(timestamp) ? null : timestamp;
  }

  duplicateList(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeOptionsMenu();

    if (!this.listId) {
      return;
    }

    this.customListService.cloneCustomList(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken,
      this.listId
    ).subscribe(res => {
      if (this.isActionSuccess(res)) {
        this.toastService.success(StringHelper.format(this.getString(this.isPublicList() ? 'listDuplicatedToMyLists' : 'listDuplicated'), this.getTitle()), 5000);
      }
    }, err => {
      Log.getInstance().d(err);
    });
  }

  shareListLink(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeOptionsMenu();

    if (!this.canManageCurrentList() || this.isPublicList()) {
      this.copyPublicListUrl();
      return;
    }

    if (!this.listId || !this.sessionToken) {
      Log.getInstance().d('No list ID or session token available for share');
      return;
    }

    const shareTab = BrowserNavigationHelper.openBlankTabForAsyncResult();
    this.customListService.getShareUrl(
      Global.getCountry(),
      Global.getStore(),
      Global.getMediaTypeForWishlistRequest(),
      this.sessionToken,
      this.listId
    ).subscribe(res => {
      const shareUrl = this.getShareUrlFromResponse(res);
      if (shareUrl) {
        BrowserNavigationHelper.navigateOpenedTabOrSameWindow(shareTab, shareUrl);
      }
      else {
        BrowserNavigationHelper.closeTabIfOpen(shareTab);
        Log.getInstance().d('Failed to get custom list share URL: ' + JSON.stringify(res));
      }
    }, err => {
      Log.getInstance().d(err);
      BrowserNavigationHelper.closeTabIfOpen(shareTab);
    });
  }

  copyPublicListUrl() {
    const url = this.getAbsoluteListDetailUrl() || window.location.href;
    this.copyTextToClipboard(url).then((copied) => {
      if (copied) {
        this.toastService.success(this.getString("urlCopiedToClipboard"));
      }
      else {
        this.toastService.error(this.getString("technicalProblems"));
      }
    });
  }

  copyTextToClipboard(text: string): Promise<boolean> {
    const navigatorRef = <any>navigator;
    if (navigatorRef && navigatorRef.clipboard && navigatorRef.clipboard.writeText) {
      return navigatorRef.clipboard.writeText(text).then(() => {
        return true;
      }, err => {
        Log.getInstance().d(err);
        return this.copyTextToClipboardFallback(text);
      });
    }

    return Promise.resolve(this.copyTextToClipboardFallback(text));
  }

  copyTextToClipboardFallback(text: string): boolean {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "readonly");
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    let copied = false;
    try {
      copied = document.execCommand("copy");
    }
    catch (err) {
      Log.getInstance().d(err);
    }

    document.body.removeChild(textArea);
    return copied;
  }

  editList(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeOptionsMenu();

    if (!this.listId || !this.canManageCurrentList()) {
      return;
    }

    this.router.navigateByUrl(this.getListsUrl() + '?editList=' + encodeURIComponent(this.listId));
  }

  getShareUrlFromResponse(res: any): string {
    if (!res) {
      return "";
    }
    if (res.additionalInfo) {
      if (res.additionalInfo.shareUrl) {
        return res.additionalInfo.shareUrl;
      }
      if (res.additionalInfo.shareURL) {
        return res.additionalInfo.shareURL;
      }
    }
    if (res.shareUrl) {
      return res.shareUrl;
    }
    if (res.shareURL) {
      return res.shareURL;
    }
    if (res.results && res.results.additionalInfo) {
      if (res.results.additionalInfo.shareUrl) {
        return res.results.additionalInfo.shareUrl;
      }
      if (res.results.additionalInfo.shareURL) {
        return res.results.additionalInfo.shareURL;
      }
    }
    if (res.results && res.results.shareUrl) {
      return res.results.shareUrl;
    }
    if (res.results && res.results.shareURL) {
      return res.results.shareURL;
    }
    return "";
  }

  isActionSuccess(res: any): boolean {
    return res === "success" ||
      (res && (res.status === "success" ||
      res.message === "success" ||
      res.success === true ||
      res.success === "true" ||
      (res.results && res.results.status === "success")));
  }

  getRankingModeValue(): any {
    if (!this.detail) {
      return null;
    }

    if (this.detail.rankingMode !== undefined && this.detail.rankingMode !== null) {
      return this.detail.rankingMode;
    }
    if (this.detail.isRanked !== undefined && this.detail.isRanked !== null) {
      return this.detail.isRanked;
    }
    if (this.detail.rankingOrder !== undefined && this.detail.rankingOrder !== null) {
      return this.detail.rankingOrder;
    }

    return null;
  }

  isRankedList(): boolean {
    const value = this.getRankingModeValue();

    if (value === true || value === 1 || value === 2 || value === "1" || value === "2") {
      return true;
    }
    if (typeof value === "string") {
      const loweredValue = value.toLowerCase();
      return loweredValue === "true" || loweredValue.indexOf("asc") !== -1 || loweredValue.indexOf("desc") !== -1;
    }

    return false;
  }

  isDescendingRanking(): boolean {
    const value = this.getRankingModeValue();

    if (value === 2 || value === "2") {
      return true;
    }
    if (typeof value === "string") {
      return value.toLowerCase().indexOf("desc") !== -1;
    }

    return false;
  }

  getRankingNumber(index: number): number {
    return this.getRankingNumberForTotal(index, this.items.length);
  }

  getRankingNumberForTotal(index: number, total: number): number {
    if (this.isDescendingRanking()) {
      return total - index;
    }
    return index + 1;
  }

  getItemTitle(item: any): string {
    if (!item) {
      return "";
    }
    return item.itemName || item.title || item.name || item.trackName || item.collectionName || "";
  }

  getItemDetailMediaType(item: any): string {
    let mediaType = "";

    if (item) {
      mediaType = this.normalizeDetailMediaType(item.itemType);
      if (!mediaType) {
        mediaType = this.normalizeDetailMediaType(item.trackType);
      }
      if (!mediaType) {
        mediaType = this.normalizeDetailMediaType(item.mediaType);
      }
    }

    if (!mediaType && this.route.snapshot && this.route.snapshot.params) {
      mediaType = this.normalizeDetailMediaType(this.route.snapshot.params["mediaType"]);
    }

    if (!mediaType) {
      mediaType = this.normalizeDetailMediaType(Global.getMediaTypeForDetailView());
    }

    return mediaType;
  }

  private normalizeDetailMediaType(value: any): string {
    if (!value) {
      return "";
    }

    const mediaType = String(value).toLowerCase().trim();

    switch (mediaType) {
      case "movie":
      case "movies":
      case "feature-movie":
      case "buymovies":
      case "rentalmovies":
        return Global.MOVIES;
      case "season":
      case "seasons":
      case "tv":
      case "tv-season":
      case "tvseason":
      case "tv-seasons":
        return Global.SEASONS;
      case "album":
      case "albums":
      case "music-album":
      case "music-albums":
        return Global.ALBUMS;
      case "single":
      case "singles":
      case "song":
      case "track":
        return Global.SINGLES;
      case "music":
        return Global.ALBUMS;
      case "audiobook":
      case "audiobooks":
      case "audio-book":
        return Global.AUDIOBOOKS;
      case "ebook":
      case "ebooks":
      case "book":
      case "books":
        return Global.EBOOKS;
      case "app":
      case "apps":
      case "software":
      case "software-package":
      case "ios-app":
        return Global.APPS;
      case "macapp":
      case "macapps":
      case "mac-app":
        return Global.MACAPPS;
      default:
        return this.normalizeCorrespondingDetailMediaType(Global.getCorrespondingMediaType(mediaType));
    }
  }

  private normalizeCorrespondingDetailMediaType(mediaType: string): string {
    if (!mediaType) {
      return "";
    }

    if (mediaType === Global.MOVIES || mediaType === Global.BUYMOVIES || mediaType === Global.RENTALMOVIES) {
      return Global.MOVIES;
    }
    if (mediaType === Global.SEASONS || mediaType === Global.TV) {
      return Global.SEASONS;
    }
    if (mediaType === Global.MUSIC) {
      return Global.ALBUMS;
    }
    if (Global.supportedDetailViewMediaTypes.indexOf(mediaType) !== -1) {
      return mediaType;
    }

    return "";
  }

  getItemId(item: any): string {
    if (!item) {
      return "";
    }

    const id = item.itemID !== undefined && item.itemID !== null ? item.itemID :
      item.itemId !== undefined && item.itemId !== null ? item.itemId :
      item.idInStore !== undefined && item.idInStore !== null ? item.idInStore :
      item.ID !== undefined && item.ID !== null ? item.ID :
      item.id !== undefined && item.id !== null ? item.id :
      item.trackId !== undefined && item.trackId !== null ? item.trackId :
      item.collectionId !== undefined && item.collectionId !== null ? item.collectionId : "";

    return String(id);
  }

  normalizeGridItems(items: Array<any>): Array<any> {
    const normalizedItems = [];
    const total = items.length;

    for (let i = 0; i < items.length; i++) {
      normalizedItems.push(this.normalizeGridItem(items[i], i, total));
    }

    return normalizedItems;
  }

  normalizeGridItem(item: any, index: number, total: number): any {
    if (!item) {
      return item;
    }

    const normalizedItem: any = {};
    for (const key in item) {
      if (item.hasOwnProperty(key)) {
        normalizedItem[key] = item[key];
      }
    }

    const id = this.getItemId(item);
    if (id && normalizedItem["idInStore"] === undefined) {
      normalizedItem["idInStore"] = id;
    }
    if (id && normalizedItem["itemID"] === undefined) {
      normalizedItem["itemID"] = id;
    }

    if (!normalizedItem["title"]) {
      normalizedItem["title"] = this.getItemTitle(item);
    }

    normalizedItem["customListOriginalIndex"] = index;

    if (!normalizedItem["artist"]) {
      normalizedItem["artist"] = this.getItemArtist(item);
    }

    const mediaType = this.getItemDetailMediaType(normalizedItem);
    normalizedItem["gridItemMedia"] = mediaType;

    const itemType = this.getItemTypeForMediaType(mediaType);
    if (itemType) {
      normalizedItem["itemType"] = itemType;
    }

    if (!normalizedItem["itemImageURL"]) {
      normalizedItem["itemImageURL"] = this.getItemImageUrl(item);
    }

    if (!normalizedItem["currency"]) {
      normalizedItem["currency"] = this.getDefaultCurrency();
    }

    this.normalizeGridItemFlags(normalizedItem);
    this.normalizeGridItemPrices(normalizedItem, mediaType);
    this.normalizeGridItemRatings(normalizedItem);

    if (this.isRankedList()) {
      normalizedItem["rank"] = this.getRankingNumberForTotal(index, total);
    }

    return normalizedItem;
  }

  getItemArtist(item: any): string {
    if (!item) {
      return "";
    }
    return item.artist || item.itemArtist || item.artistName || item.subtitle || item.itemSubtitle || item.collectionArtistName || "";
  }

  getItemImageUrl(item: any): string {
    if (!item) {
      return "";
    }

    const image = item.artworkUrl600 || item.artworkUrl512 || item.artworkUrl100 || item.artworkUrl ||
      item.imageUrl || item.imageURL || item.image || item.posterUrl || item.posterURL ||
      item.poster || item.thumbnail || item.thumb || item.itemImageUrl || item.itemImageURL ||
      item.itemImage || item.itemArtworkUrl || item.itemArtworkURL || item.itemArtwork;

    if (!image || typeof image !== "string") {
      return "";
    }

    return image;
  }

  getItemTypeForMediaType(mediaType: string): string {
    switch (mediaType) {
      case Global.MOVIES:
        return "movie";
      case Global.SEASONS:
        return "season";
      case Global.ALBUMS:
        return "album";
      case Global.SINGLES:
        return "single";
      case Global.AUDIOBOOKS:
        return "audiobook";
      case Global.EBOOKS:
        return "ebook";
      case Global.APPS:
        return "app";
      case Global.MACAPPS:
        return "macapp";
      default:
        return "";
    }
  }

  getDefaultCurrency(): string {
    const country = Global.countries[Global.getCountry()];
    return country && country.currency ? country.currency : "";
  }

  normalizeGridItemFlags(item: any) {
    const booleanFields = ["isPreorder", "isBundle", "isMovieBundle", "isSeasonBundle", "has4K", "has3D", "hasAtmos", "iTunesExtra", "moviesAnywhere", "showHD", "priceHd"];
    for (let i = 0; i < booleanFields.length; i++) {
      const field = booleanFields[i];
      if (item[field] === "0" || item[field] === 0 || item[field] === false || item[field] === null) {
        item[field] = false;
      }
      else if (item[field] === "1" || item[field] === 1 || item[field] === true) {
        item[field] = true;
      }
    }
  }

  normalizeGridItemRatings(item: any) {
    if (item.imdbRating === undefined) {
      item.imdbRating = this.getFirstNumericValue(item, ["imdb", "imdb_score"]);
    }
    if (item.tomatoesScore === undefined) {
      item.tomatoesScore = this.getFirstNumericValue(item, ["rottenTomatoesRating", "rottenTomatoesScore", "rtRating", "rtScore"]);
    }
    if (item.metacritic === undefined) {
      item.metacritic = this.getFirstNumericValue(item, ["metacriticRating", "metacriticScore"]);
    }
    if (item.letterboxdRating === undefined) {
      item.letterboxdRating = this.getFirstNumericValue(item, ["letterboxdScore"]);
    }

    const numericFields = ["imdbRating", "tomatoesScore", "metacritic", "letterboxdRating", "userRating", "userRatingCount"];
    for (let i = 0; i < numericFields.length; i++) {
      const field = numericFields[i];
      if (item[field] !== undefined && item[field] !== null && item[field] !== "") {
        const numericValue = Number(item[field]);
        if (!isNaN(numericValue)) {
          item[field] = numericValue;
        }
      }
    }
  }

  normalizeGridItemPrices(item: any, mediaType: string) {
    const currentPrice = this.getFirstPriceValue(item, [
      "price",
      "currentPrice",
      "currentPriceValue",
      "priceValue",
      "itemPriceAmount",
      "trackPrice",
      "collectionPrice",
      "itemPriceLabel",
      "priceLabel",
      "formattedPrice",
      "currentPriceLabel"
    ]);
    let oldPrice = this.getFirstPriceValue(item, [
      "priceBefore",
      "priceHdBefore",
      "priceSdBefore",
      "oldPrice",
      "previousPrice",
      "regularPrice",
      "listPrice",
      "itemPriceBefore",
      "itemPriceHDBefore",
      "itemPriceSdBefore",
      "itemOldPriceAmount",
      "oldPriceLabel",
      "previousPriceLabel"
    ]);

    if (currentPrice !== null && currentPrice !== undefined && item.price === undefined) {
      item.price = currentPrice;
    }
    if (oldPrice === null || oldPrice === undefined) {
      oldPrice = this.getPreviousPriceFromHistory(item);
    }

    if (this.isMovieOrSeason(mediaType)) {
      this.normalizeMovieOrSeasonPrices(item, currentPrice, oldPrice);
    }
    else {
      if (oldPrice !== null && oldPrice !== undefined && item.priceBefore === undefined) {
        item.priceBefore = oldPrice;
      }
      if (currentPrice !== null && oldPrice !== null && item.priceDropIndicator === undefined) {
        item.priceDropIndicator = currentPrice < oldPrice ? -1 : (currentPrice > oldPrice ? 1 : 0);
      }
      if (currentPrice !== null && oldPrice !== null && item.priceIsBest === undefined) {
        item.priceIsBest = 0;
      }
    }
  }

  normalizeMovieOrSeasonPrices(item: any, currentPrice: number, oldPrice: number) {
    const currentKey = PriceHelper.getCompletePriceKey("", "offerslist", item, false);
    const oldKey = PriceHelper.getCompletePriceKey("Before", "offerslist", item, false);
    const dropKey = PriceHelper.getCompletePriceKey("DropIndicator", "offerslist", item, false);
    const isBestKey = PriceHelper.getCompletePriceKey("IsBest", "offerslist", item, false);

    if (currentPrice !== null && currentPrice !== undefined && item[currentKey] === undefined) {
      item[currentKey] = currentPrice;
    }
    if (oldPrice !== null && oldPrice !== undefined && item[oldKey] === undefined) {
      item[oldKey] = oldPrice;
    }
    if (currentPrice !== null && oldPrice !== null && item[dropKey] === undefined) {
      item[dropKey] = currentPrice < oldPrice ? -1 : (currentPrice > oldPrice ? 1 : 0);
    }
    if (currentPrice !== null && oldPrice !== null && item[isBestKey] === undefined) {
      item[isBestKey] = 0;
    }
  }

  isMovieOrSeason(mediaType: string): boolean {
    return mediaType === Global.MOVIES || mediaType === Global.SEASONS;
  }

  getPreviousPriceFromHistory(item: any): number {
    return PriceHelper.getFirstAvailablePreviousPriceFromEvolution(item, [
      "itemPriceHDHistory",
      "itemPriceHistory",
      "priceHistory",
      "priceHdHistory",
      "priceSdHistory",
      "priceHdEvolution",
      "priceSdEvolution",
      "priceEvolution"
    ]);
  }

  getFirstPriceValue(item: any, keys: Array<string>): number {
    for (let i = 0; i < keys.length; i++) {
      const value = this.parsePriceValue(item[keys[i]]);
      if (value !== null && value !== undefined) {
        return value;
      }
    }

    return null;
  }

  parsePriceValue(value: any): number {
    if (value === undefined || value === null || value === "") {
      return null;
    }

    if (typeof value === "number") {
      return value;
    }

    if (typeof value !== "string") {
      return null;
    }

    const lowerValue = value.toLowerCase();
    if (lowerValue.indexOf("free") !== -1) {
      return 0;
    }

    let normalizedValue = value.replace(/[^0-9,\.\-]/g, "");
    if (!normalizedValue) {
      return null;
    }

    const commaIndex = normalizedValue.lastIndexOf(",");
    const pointIndex = normalizedValue.lastIndexOf(".");
    if (commaIndex > pointIndex) {
      normalizedValue = normalizedValue.replace(/\./g, "").replace(",", ".");
    }
    else {
      normalizedValue = normalizedValue.replace(/,/g, "");
    }

    const parsedValue = parseFloat(normalizedValue);
    return isNaN(parsedValue) ? null : parsedValue;
  }

  getStore(): string {
    return Global.getStore();
  }

  addToWishlist(item: any) {
    const id = this.getListActionItemId(item);
    const itemType = this.getListActionItemType(item);
    const ignoreSdPrice = this.shouldIgnoreSdPriceForMediaType(itemType);
    this.wishlistService.addItem(Global.getCountry(), Global.getStore(), itemType,
      this.getSessionToken(), id, ignoreSdPrice).subscribe(res => {
        this.handleWishlistActionResult(res);
      }, err => {
        Log.getInstance().d(err);
      });
  }

  removeFromWishlist(item: any) {
    const id = this.getListActionItemId(item);
    const itemType = this.getListActionItemType(item);
    this.wishlistService.removeItem(Global.getCountry(), Global.getStore(), itemType,
      this.getSessionToken(), id).subscribe(res => {
        this.handleWishlistActionResult(res);
      }, err => {
        Log.getInstance().d(err);
      });
  }

  addToOwnlist(item: any) {
    const id = this.getListActionItemId(item);
    const itemType = this.getListActionItemType(item);
    this.ownlistService.addItem(Global.getCountry(), Global.getStore(), itemType,
      this.getSessionToken(), id).subscribe(res => {
        this.handleOwnlistActionResult(res);
      }, err => {
        Log.getInstance().d(err);
      });
  }

  removeFromOwnlist(item: any) {
    const id = this.getListActionItemId(item);
    const itemType = this.getListActionItemType(item);
    this.ownlistService.removeItem(Global.getCountry(), Global.getStore(), itemType,
      this.getSessionToken(), id).subscribe(res => {
        this.handleOwnlistActionResult(res);
      }, err => {
        Log.getInstance().d(err);
      });
  }

  addToBlocklist(item: any) {
    const id = this.getListActionItemId(item);
    const itemType = this.getListActionItemType(item);
    this.blocklistService.addItem(Global.getCountry(), Global.getStore(), itemType,
      this.getSessionToken(), id).subscribe(res => {
        this.handleBlocklistActionResult(res);
      }, err => {
        Log.getInstance().d(err);
      });
  }

  removeFromBlocklist(item: any) {
    const id = this.getListActionItemId(item);
    const itemType = this.getListActionItemType(item);
    this.blocklistService.removeItem(Global.getCountry(), Global.getStore(), itemType,
      this.getSessionToken(), id).subscribe(res => {
        this.handleBlocklistActionResult(res);
      }, err => {
        Log.getInstance().d(err);
      });
  }

  getSessionToken(): string {
    this.sessionToken = StorageHelper.getInstance().get(StorageHelper.keys.sessionToken) || "";
    return this.sessionToken;
  }

  getListActionItemId(item: any): string {
    return this.getItemId(item);
  }

  getListActionItemType(item: any): string {
    let itemType = item && item.itemType ? Global.getPluralItemType(item.itemType) : "";

    if (itemType === Global.MOVIES) {
      return Global.BUYMOVIES;
    }

    if (!itemType && item) {
      itemType = Global.getItemTypeForWishList(item.gridItemMedia || this.getItemDetailMediaType(item));
    }

    return itemType;
  }

  handleWishlistActionResult(res: any) {
    if (this.handleCouldntLoadUser(res)) {
      return;
    }
    this.updateLocalShortWishlist(this.getSessionToken());
  }

  handleOwnlistActionResult(res: any) {
    if (this.handleCouldntLoadUser(res)) {
      return;
    }
    this.updateLocalShortOwnlist(this.getSessionToken());
  }

  handleBlocklistActionResult(res: any) {
    if (this.handleCouldntLoadUser(res)) {
      return;
    }
    this.updateLocalShortBlocklist(this.getSessionToken());
  }

  handleCouldntLoadUser(res: any): boolean {
    if (res && res["message"] === BaseService.COULDNT_LOAD_USER) {
      Global.clearSaveUserInfos();
      this.showLoginRequired = true;
      return true;
    }
    return false;
  }

  updateLocalShortWishlist(sessionToken: string) {
    if (!sessionToken) {
      return;
    }

    this.wishlistService.getShortWishlist(
      Global.getCountry(),
      Global.getStore(),
      sessionToken
    ).subscribe(res => {
      this.saveShortListResults(res, StorageHelper.keys.shortWishlist,
        () => ListHelper.clearWishlistCache(), 'Failed to save wishlist to storage - storage may be full');
    }, err => {
      Log.getInstance().d(err);
    });
  }

  updateLocalShortOwnlist(sessionToken: string) {
    if (!sessionToken) {
      return;
    }

    this.ownlistService.getShortItemList(
      sessionToken,
      Global.getStore(),
      Global.getCountry()
    ).subscribe(res => {
      this.saveShortListResults(res, StorageHelper.keys.shortOwnlist,
        () => ListHelper.clearOwnlistCache(), 'Failed to save ownlist to storage - storage may be full');
    }, err => {
      Log.getInstance().d(err);
    });
  }

  updateLocalShortBlocklist(sessionToken: string) {
    if (!sessionToken) {
      return;
    }

    this.blocklistService.getShortBlocklist(
      Global.getCountry(),
      Global.getStore(),
      sessionToken
    ).subscribe(res => {
      this.saveShortListResults(res, StorageHelper.keys.shortBlocklist,
        () => ListHelper.clearBlocklistCache(), 'Failed to save blocklist to storage - storage may be full');
    }, err => {
      Log.getInstance().d(err);
    });
  }

  saveShortListResults(res: any, storageKey: string, clearCache: () => void, saveErrorMessage: string) {
    if (this.handleCouldntLoadUser(res)) {
      return;
    }

    if (res && res["results"]) {
      const shortList = StorageHelper.getInstance().get(storageKey) || {};
      shortList[Global.getStore()] = res["results"];
      const saved = StorageHelper.getInstance().save(storageKey, shortList);
      if (saved) {
        clearCache();
      }
      else {
        Log.getInstance().d(saveErrorMessage);
      }
    }
  }

  shouldIgnoreSdPriceForMediaType(mediaType: string): boolean {
    const setting = StorageHelper.getInstance().get(StorageHelper.keys.ignoreSdPriceOnWishlist);
    if (setting === false) {
      return false;
    }

    const movieAndTvTypes = ['movies', 'buymovies', 'rentalmovies', 'seasons', 'tvSeasons'];
    return movieAndTvTypes.indexOf(mediaType) !== -1;
  }

}
